/** Saved replies: the canned answers offered in the Reply panel. Admins and operators write; only admins archive. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { apiErrorText } from '../../lib/apiErrorText'
import { fmtDateTime } from '../../lib/format'
import { TITLE_LABELS, TITLE_ORDER, type SavedReply } from '../../lib/supportDesk'
import { deskOff, useDeskMe } from './SupportQueue'

const FIELD = 'bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[14px]'

type Draft = { id: number | null; name: string; publication: string; body_md: string }
const BLANK: Draft = { id: null, name: '', publication: '', body_md: '' }

function Editor({ draft, onDone }: { draft: Draft; onDone: () => void }) {
  const qc = useQueryClient()
  const [d, setD] = useState(draft)
  const save = useMutation({
    mutationFn: () => {
      const body = { name: d.name.trim(), publication: d.publication || null, body_md: d.body_md }
      return d.id == null
        ? api.post<SavedReply>('/api/support/saved-replies', body)
        : api.patch<SavedReply>(`/api/support/saved-replies/${d.id}`, body)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['support'] }); onDone() },
  })
  const ok = d.name.trim().length > 0 && d.body_md.trim().length > 0
  return (
    <form className="border border-ink-500 p-4 space-y-3" onSubmit={e => { e.preventDefault(); if (ok) save.mutate() }}>
      <div className="label">{d.id == null ? 'New saved reply' : 'Edit saved reply'}</div>
      <label className="block">
        <span className="label">Name</span>
        <input className={`${FIELD} mt-1 w-full`} maxLength={120} value={d.name} onChange={e => setD({ ...d, name: e.target.value })} />
      </label>
      <label className="block">
        <span className="label">Title</span>
        <select className={`${FIELD} mt-1 block`} value={d.publication} onChange={e => setD({ ...d, publication: e.target.value })}>
          <option value="">All titles</option>
          {TITLE_ORDER.map(t => <option key={t} value={t}>{TITLE_LABELS[t]}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="label">Reply</span>
        <textarea rows={10} maxLength={20000} className={`${FIELD} mt-1 w-full`} value={d.body_md}
          onChange={e => setD({ ...d, body_md: e.target.value })} />
      </label>
      <p className="text-xs text-paper-trace">
        Same formatting as the Reply panel: **bold**, "- " bullets, "1. " steps, [link](https://…), one [button: Label](https://…).
      </p>
      {save.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(save.error)}</p>}
      <div className="flex gap-3">
        <button type="submit" className="label border border-amber-glow text-amber-glow px-3 py-2 disabled:opacity-40"
          disabled={!ok || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        <button type="button" className="label px-3 py-2" onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}

export function SavedRepliesPage() {
  const qc = useQueryClient()
  const { canAct, isAdmin } = useDeskMe()
  const [editing, setEditing] = useState<Draft | null>(null)
  const replies = useQuery<SavedReply[]>({
    queryKey: ['support', 'saved-replies'], queryFn: () => api.get<SavedReply[]>('/api/support/saved-replies'), retry: false,
  })
  const archive = useMutation({
    mutationFn: (id: number) => api.patch<SavedReply>(`/api/support/saved-replies/${id}`, { archived: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support'] }),
  })

  if (deskOff(replies.error)) {
    return (
      <div className="p-8 max-w-[640px]">
        <div className="label mb-2">Customer Support</div>
        <p className="font-serif text-2xl text-paper">Customer Support desk is not switched on</p>
      </div>
    )
  }
  const list = replies.data ?? []

  return (
    <div className="p-8 space-y-6 max-w-[960px]">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Members · customer support</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">Saved replies</h1>
        </div>
        <div className="flex gap-6 items-center">
          <Link to="/support/setup" className="label-amber hover:underline">Support setup</Link>
          {canAct && !editing && (
            <button className="label border border-amber-glow text-amber-glow px-3 py-2" onClick={() => setEditing(BLANK)}>New saved reply</button>
          )}
        </div>
      </header>

      {editing && <Editor key={editing.id ?? 'new'} draft={editing} onDone={() => setEditing(null)} />}
      {replies.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(replies.error)}</p>}
      {replies.isLoading && <p className="text-paper-trace text-sm">Loading…</p>}
      {replies.isSuccess && list.length === 0 && <p className="text-paper-trace text-sm">No saved replies yet.</p>}
      {archive.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(archive.error)}</p>}

      <ul className="divide-y divide-ink-600">
        {list.map(r => (
          <li key={r.id} className="py-4 flex gap-4 items-start">
            <div className="min-w-0 flex-1">
              <div className="text-paper">{r.name}
                <span className="label ml-3">{r.publication ? TITLE_LABELS[r.publication] ?? r.publication : 'All titles'}</span>
              </div>
              <p className="mt-1 text-sm text-paper-dim whitespace-pre-wrap line-clamp-3">{r.body_md}</p>
              <div className="label mt-1 text-paper-trace">
                {r.created_by ?? 'Former staff'}{r.updated_at ? ` · updated ${fmtDateTime(r.updated_at)}` : ''}
              </div>
            </div>
            {canAct && (
              <div className="flex gap-4 shrink-0">
                <button className="label-amber hover:underline" aria-label={`Edit ${r.name}`}
                  onClick={() => setEditing({ id: r.id, name: r.name, publication: r.publication ?? '', body_md: r.body_md })}>Edit</button>
                {isAdmin && (
                  <button className="label hover:underline" aria-label={`Archive ${r.name}`} disabled={archive.isPending}
                    onClick={() => { if (window.confirm(`Archive "${r.name}"? It stops appearing in the Reply panel.`)) archive.mutate(r.id) }}>
                    Archive
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
